import { useRef, useImperativeHandle } from 'react'
import AuthContext from './AuthContext'
import appConfig from '@/configs/app.config'
import {
    useSessionUser,
    useToken,
    clearSession,
    tokenStorage,
} from '@/store/authStore'
import {
    apiAuthFirebase,
    apiFirebaseMigrate,
    apiRequestPasswordReset,
    apiResendVerification,
    apiSignOut,
    apiTwoFactorVerify,
} from '@/services/AuthService'
import {
    firebaseSignInWithEmail,
    firebaseSignInWithGoogle,
    firebaseSignInWithMicrosoft,
    firebaseSignUpWithEmail,
} from './firebase'
import { REDIRECT_URL_KEY } from '@/constants/app.constant'
import { pendingInvitePath } from '@/utils/pendingInvite'
import { useNavigate } from 'react-router-dom'
import {
    ACCESS_PENDING,
    EMAIL_NOT_VERIFIED,
    TWO_FACTOR_EXPIRED,
    TWO_FACTOR_INVALID,
    TWO_FACTOR_LOCKED,
    TWO_FACTOR_REQUIRED,
} from '@/@types/auth'
import type {
    AccessPendingResponse,
    TwoFactorRequiredResponse,
    TwoFactorResult,
    TwoFactorState,
    WaitlistState,
    SignInCredential,
    SignUpCredential,
    AuthResult,
    FirebaseExchangeRequest,
    OauthSignInCallbackPayload,
    User,
    Token,
} from '@/@types/auth'
import type { ReactNode, Ref } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import type { AxiosError } from 'axios'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

type AuthProviderProps = { children: ReactNode }

export type IsolatedNavigatorRef = {
    navigate: NavigateFunction
}

/**
 * Exposes `navigate` without subscribing the provider to route changes —
 * `useNavigate` in AuthProvider itself would re-render the whole authenticated
 * tree on every navigation.
 */
const IsolatedNavigator = ({ ref }: { ref: Ref<IsolatedNavigatorRef> }) => {
    const navigate = useNavigate()

    useImperativeHandle(ref, () => {
        return {
            navigate,
        }
    }, [navigate])

    return <></>
}

/**
 * Firebase error codes that might mean "this account was carried over and has
 * no Firebase password yet".
 *
 * Might equally mean "wrong password" — with email-enumeration protection on
 * (it must be; it is a Firebase console setting) Firebase deliberately collapses
 * both into `auth/invalid-credential` so the browser cannot tell whether an
 * address exists. We cannot distinguish them here and do not try: the migration
 * call is harmless when it is not needed, and it answers 204 either way.
 */
const MIGRATABLE_CODES = new Set([
    'auth/user-not-found',
    'auth/invalid-credential',
    'auth/invalid-login-credentials',
    'auth/wrong-password',
])

/**
 * Firebase codes worth their own sentence, mapped to translation keys.
 *
 * **Never render `FirebaseError.message`.** It is English whatever the user's
 * language, and it reads like a stack trace — `"Firebase: Error
 * (auth/invalid-credential)."` — on the one screen where a clear error matters
 * most. Anything not listed falls through to the generic message.
 */
const FIREBASE_ERROR_KEYS: Record<string, string> = {
    'auth/invalid-credential': 'auth.errors.badCredentials',
    'auth/invalid-login-credentials': 'auth.errors.badCredentials',
    'auth/user-not-found': 'auth.errors.badCredentials',
    'auth/wrong-password': 'auth.errors.badCredentials',
    'auth/user-disabled': 'auth.errors.accountDisabled',
    'auth/too-many-requests': 'auth.errors.tooManyAttempts',
    'auth/email-already-in-use': 'auth.errors.emailInUse',
    'auth/weak-password': 'auth.errors.weakPassword',
    'auth/network-request-failed': 'auth.errors.networkFailed',
    'auth/popup-closed-by-user': 'auth.errors.popupClosed',
    'auth/cancelled-popup-request': 'auth.errors.popupClosed',
    'auth/popup-blocked': 'auth.errors.popupBlocked',
    'auth/account-exists-with-different-credential':
        'auth.errors.differentProvider',
}

/**
 * Pulls a readable message out of a DRF error body.
 *
 * DRF reports field errors as `{field: [messages]}` and non-field failures as
 * `{detail: "..."}`. Without this the user sees "[object Object]" on the one
 * screen where a clear error matters most.
 */
const extractErrorMessage = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError<Record<string, unknown>>)?.response?.data

    if (!data) {
        return (error as Error)?.message || fallback
    }

    if (typeof data.detail === 'string') {
        return data.detail
    }

    const firstFieldError = Object.values(data).find(
        (value) => Array.isArray(value) && typeof value[0] === 'string',
    ) as string[] | undefined

    return firstFieldError?.[0] ?? fallback
}

/** The backend's machine-readable reason, when it sent one. */
const errorCode = (error: unknown): string | undefined => {
    const data = (error as AxiosError<{ code?: string }>)?.response?.data
    return typeof data?.code === 'string' ? data.code : undefined
}

/**
 * One message for either kind of failure.
 *
 * A sign-in crosses two systems — Firebase in the browser, then our API — and
 * the user does not care which one said no. Firebase failures carry a `code`;
 * ours carry a DRF body.
 */
const mapAuthError = (
    error: unknown,
    t: TFunction,
    fallbackKey: string,
): string => {
    const code = (error as { code?: string })?.code
    if (code && FIREBASE_ERROR_KEYS[code]) {
        return t(FIREBASE_ERROR_KEYS[code])
    }
    if (code?.startsWith('auth/')) {
        return t(fallbackKey)
    }
    return extractErrorMessage(error, t(fallbackKey))
}

function AuthProvider({ children }: AuthProviderProps) {
    // Only the *fallbacks* below are translated. When the server sends a
    // message it wins, and those are English — the backend is not localised
    // (see the root CLAUDE.md note on scope). A wrong-language sentence from
    // the server beats no sentence at all on the one screen where a clear
    // error matters most.
    const { t } = useTranslation()
    const signedIn = useSessionUser((state) => state.session.signedIn)
    const user = useSessionUser((state) => state.user)
    const setUser = useSessionUser((state) => state.setUser)
    const setSessionSignedIn = useSessionUser(
        (state) => state.setSessionSignedIn,
    )
    const { token, setToken } = useToken()

    const authenticated = Boolean(token && signedIn)

    const navigatorRef = useRef<IsolatedNavigatorRef>(null)

    const redirect = () => {
        const search = window.location.search
        const params = new URLSearchParams(search)
        const redirectUrl = params.get(REDIRECT_URL_KEY)

        // An invitation opened before signing in comes next when nothing
        // more specific was asked for — see `utils/pendingInvite`.
        navigatorRef.current?.navigate(
            redirectUrl ||
                pendingInvitePath() ||
                appConfig.authenticatedEntryPath,
        )
    }

    const handleSignIn = (tokens: Token, user?: User) => {
        setToken(tokens.accessToken, tokens.refreshToken)
        setSessionSignedIn(true)

        if (user) {
            setUser(user)
        }
    }

    /**
     * The one place a Firebase identity becomes a TrackHeros session.
     *
     * Every sign-in path — password, Google, Microsoft — ends here, so the
     * session-establishing behaviour is written once: store both tokens, and
     * send a brand-new account to `/welcome` for the job-role question that a
     * one-click sign-up otherwise skips entirely.
     *
     * An empty `idToken` means the popup fell back to a redirect and the page
     * is already navigating away; there is nothing to exchange.
     */
    const exchange = async (payload: FirebaseExchangeRequest) => {
        if (!payload.idToken) {
            return false
        }

        const resp = await apiAuthFirebase(payload)
        handleSignIn(
            {
                accessToken: resp.token,
                refreshToken: resp.refreshToken,
            },
            resp.user,
        )

        if (resp.isNewUser) {
            navigatorRef.current?.navigate('/welcome')
        } else {
            redirect()
        }

        return true
    }

    /**
     * Routes the backend's "confirm your address first" to the screen that
     * explains it, instead of showing it as a failure.
     *
     * Returns true when it handled the error.
     */
    const handleUnverified = (error: unknown, email: string): boolean => {
        if (errorCode(error) !== EMAIL_NOT_VERIFIED) {
            return false
        }
        navigatorRef.current?.navigate(
            `/verify-email?email=${encodeURIComponent(email)}`,
        )
        return true
    }

    /**
     * Routes the private beta's "your account is waiting" to the waitlist
     * screen, instead of showing it as a failed sign-in.
     *
     * It is not one: the account was created and the sign-in was valid, and
     * telling somebody "unable to sign in" would send them round the sign-up
     * form again. Checked on every path — password, Google and Microsoft all
     * reach the same gate.
     *
     * The address travels in router state rather than the query string, so it
     * stays out of the URL bar and browser history; the page reads without it.
     *
     * Returns true when it handled the error.
     */
    const handlePending = (error: unknown): boolean => {
        if (errorCode(error) !== ACCESS_PENDING) {
            return false
        }
        const data = (error as AxiosError<Partial<AccessPendingResponse>>)
            ?.response?.data
        const state: WaitlistState = {
            email: typeof data?.email === 'string' ? data.email : undefined,
            isNewRequest: data?.isNewRequest !== false,
        }
        navigatorRef.current?.navigate('/waitlist', { state })
        return true
    }

    /**
     * Routes "this account has two-factor on" to the code screen.
     *
     * Not a failure either: the first step worked, and the backend has issued
     * a challenge instead of a session. The challenge rides in router state —
     * never the URL, where it would land in history — and the sign-in page's
     * query string is carried along, because that is where `redirectUrl`
     * lives and `redirect()` reads it once the code is accepted.
     *
     * Returns true when it handled the error.
     */
    const handleTwoFactor = (error: unknown): boolean => {
        if (errorCode(error) !== TWO_FACTOR_REQUIRED) {
            return false
        }
        const data = (error as AxiosError<Partial<TwoFactorRequiredResponse>>)
            ?.response?.data
        const state: TwoFactorState = {
            challenge: data?.challenge,
            email: data?.email,
            expiresIn: data?.expiresIn,
        }
        navigatorRef.current?.navigate(
            { pathname: '/two-factor', search: window.location.search },
            { state },
        )
        return true
    }

    /** Every sign-in can be turned into one of these screens instead of an error. */
    const handleInterstitial = (error: unknown): boolean =>
        handlePending(error) || handleTwoFactor(error)

    const signIn = async (values: SignInCredential): AuthResult => {
        try {
            let idToken: string

            try {
                idToken = await firebaseSignInWithEmail(
                    values.email,
                    values.password,
                )
            } catch (error) {
                if (
                    !MIGRATABLE_CODES.has(
                        (error as { code?: string })?.code ?? '',
                    )
                ) {
                    throw error
                }

                // Django's PBKDF2 hashes cannot be imported into Firebase —
                // Django 5.2 uses 1,000,000 rounds and Firebase's importer caps
                // at 120,000 — so an account carried over from before Firebase
                // has no Firebase password until the first time its owner types
                // the right one. The backend verifies against the old hash and
                // sets it.
                //
                // It answers 204 whatever happened, deliberately, so an
                // unauthenticated endpoint that takes a password is not also an
                // oracle. The retry below is therefore the only thing that can
                // tell us whether it worked.
                //
                // EXACTLY ONE retry. Never a loop: a genuinely wrong password
                // fails both times, and looping would lock the account out
                // against Firebase's own rate limit instead of showing an error.
                await apiFirebaseMigrate({
                    email: values.email,
                    password: values.password,
                })
                idToken = await firebaseSignInWithEmail(
                    values.email,
                    values.password,
                )
            }

            await exchange({ idToken })
            return { status: 'success', message: '' }
        } catch (errors) {
            if (
                handleUnverified(errors, values.email) ||
                handleInterstitial(errors)
            ) {
                return { status: 'success', message: '' }
            }
            return {
                status: 'failed',
                message: mapAuthError(errors, t, 'auth.errors.badCredentials'),
            }
        }
    }

    const signUp = async (values: SignUpCredential): AuthResult => {
        try {
            const idToken = await firebaseSignUpWithEmail(
                values.email,
                values.password,
            )

            // Usually refused as unverified (a fresh address is not confirmed
            // yet) and handled below. The account is created by the first
            // exchange after confirmation, and `/welcome` — which asks the
            // job-role question — follows from there, or the waitlist does
            // while the private beta is on.
            await exchange({ idToken })
            return { status: 'success', message: '' }
        } catch (errors) {
            if (
                handleUnverified(errors, values.email) ||
                handlePending(errors)
            ) {
                return { status: 'success', message: '' }
            }
            return {
                status: 'failed',
                message: mapAuthError(errors, t, 'auth.errors.signUpFailed'),
            }
        }
    }

    /**
     * Sign in with a federated provider.
     *
     * Deliberately the same shape as `signIn`: the caller is a button, and the
     * fact that the credential came from Google or Microsoft rather than a
     * password field changes nothing about what happens afterwards.
     */
    const signInWithProvider = async (
        getToken: () => Promise<string>,
        fallbackKey: string,
    ): AuthResult => {
        try {
            await exchange({ idToken: await getToken() })
            return { status: 'success', message: '' }
        } catch (errors) {
            if (handleInterstitial(errors)) {
                return { status: 'success', message: '' }
            }
            return {
                status: 'failed',
                message: mapAuthError(errors, t, fallbackKey),
            }
        }
    }

    const signInWithGoogle = async (): AuthResult =>
        signInWithProvider(firebaseSignInWithGoogle, 'auth.errors.googleFailed')

    const signInWithMicrosoft = async (): AuthResult =>
        signInWithProvider(
            firebaseSignInWithMicrosoft,
            'auth.errors.microsoftFailed',
        )

    /**
     * Ask for a reset link. **Reports success whatever happened.**
     *
     * The backend answers 204 for an unknown address as well as a known one, so
     * that it cannot be used to discover who has an account. Surfacing a
     * network failure here would undo that for anyone paying attention to
     * timing, so the only thing this can honestly say is "if that address has
     * an account, we have sent a link".
     */
    const sendPasswordReset = async (email: string): AuthResult => {
        try {
            await apiRequestPasswordReset(email)
        } catch {
            // Swallowed on purpose. See above.
        }
        return { status: 'success', message: '' }
    }

    const resendVerificationEmail = async (email: string): AuthResult => {
        try {
            await apiResendVerification(email)
        } catch {
            // Same reasoning.
        }
        return { status: 'success', message: '' }
    }

    /**
     * The second step. On success it establishes the session exactly as
     * `exchange` does — the response is a sign-in's — so `/welcome` and
     * `redirectUrl` behave the same whichever way somebody got here.
     */
    const verifyTwoFactor = async (
        challenge: string,
        code: string,
    ): TwoFactorResult => {
        try {
            const resp = await apiTwoFactorVerify({ challenge, code })
            handleSignIn(
                {
                    accessToken: resp.token,
                    refreshToken: resp.refreshToken,
                },
                resp.user,
            )
            if (resp.isNewUser) {
                navigatorRef.current?.navigate('/welcome')
            } else {
                redirect()
            }
            return { status: 'success', message: '' }
        } catch (errors) {
            if (handlePending(errors)) {
                return { status: 'success', message: '' }
            }
            const code = errorCode(errors)
            if (code === TWO_FACTOR_EXPIRED) {
                return {
                    status: 'failed',
                    reason: 'expired',
                    message: t('auth.twoFactor.errors.expired'),
                }
            }
            if (code === TWO_FACTOR_LOCKED) {
                const data = (errors as AxiosError<{ retryAfter?: number }>)
                    ?.response?.data
                return {
                    status: 'failed',
                    reason: 'locked',
                    retryAfter: data?.retryAfter,
                    message: t('auth.twoFactor.errors.locked'),
                }
            }
            if (code === TWO_FACTOR_INVALID) {
                return {
                    status: 'failed',
                    reason: 'invalid',
                    message: t('auth.twoFactor.errors.invalid'),
                }
            }
            return {
                status: 'failed',
                message: mapAuthError(
                    errors,
                    t,
                    'auth.twoFactor.errors.failed',
                ),
            }
        }
    }

    const signOut = async () => {
        const refreshToken = tokenStorage.refresh

        try {
            if (refreshToken) {
                await apiSignOut(refreshToken)
            }
        } finally {
            // In `finally` deliberately: if the sign-out request fails — offline,
            // server down, token already expired — the user still asked to be
            // signed out, and leaving their session live on this machine is the
            // worse outcome. The refresh token may survive server-side; the
            // access token expires on its own.
            //
            // clearSession() also ends the Firebase session, which matters more
            // on a shared machine than it looks: without it a tab-scoped
            // Firebase session outlives our own teardown.
            clearSession()
            navigatorRef.current?.navigate(appConfig.unAuthenticatedEntryPath)
        }
    }

    const oAuthSignIn = (
        callback: (payload: OauthSignInCallbackPayload) => void,
    ) => {
        callback({
            onSignIn: handleSignIn,
            redirect,
        })
    }

    return (
        <AuthContext.Provider
            value={{
                authenticated,
                user,
                signIn,
                signUp,
                signOut,
                signInWithGoogle,
                signInWithMicrosoft,
                sendPasswordReset,
                resendVerificationEmail,
                verifyTwoFactor,
                oAuthSignIn,
            }}
        >
            {children}
            <IsolatedNavigator ref={navigatorRef} />
        </AuthContext.Provider>
    )
}

export default AuthProvider
